import { NgClass } from '@angular/common';
import { Component, HostListener, input, output, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import {
  NOTIFICATION_TONE_ICON_CLASSES,
  NOTIFICATION_TONE_PILL_CLASSES,
  NOTIFICATION_TONE_TEXT_CLASSES,
  NotificationTone,
  notificationIconToneClass,
  notificationTextToneClass,
} from 'shared/utils/notification-tone.util';

export interface NotificationDropdownItem {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly iconClass: string;
  readonly tone: NotificationTone;
  readonly highlight?: string;
  readonly highlightTone?: NotificationTone;
  readonly actionUrl?: string;
}

@Component({
  selector: 'ui-notifications-dropdown',
  standalone: true,
  imports: [NgClass, RouterLink],
  templateUrl: './notifications-dropdown.html',
  host: {
    class: 'inline-flex',
  },
})
export class NotificationsDropdown {
  readonly items = input.required<readonly NotificationDropdownItem[]>();
  readonly unreadCount = input(0);
  readonly viewAllRoute = input('/notifications');

  readonly dismiss = output<string>();
  readonly navigate = output<{ id: string; actionUrl: string }>();

  protected readonly open = signal(false);

  protected readonly toneIconClasses = NOTIFICATION_TONE_ICON_CLASSES;
  protected readonly toneTextClasses = NOTIFICATION_TONE_TEXT_CLASSES;
  protected readonly unreadPillClass = NOTIFICATION_TONE_PILL_CLASSES.info;

  protected handleToggle(): void {
    this.open.update((value) => !value);
  }

  protected handleClose(): void {
    this.open.set(false);
  }

  protected handleDismiss(id: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.dismiss.emit(id);
  }

  protected handleNavigate(
    id: string,
    actionUrl: string | undefined,
    event: Event,
  ): void {
    if (!actionUrl) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.navigate.emit({ id, actionUrl });
    this.handleClose();
  }

  protected iconToneClass(tone: NotificationTone): string {
    return notificationIconToneClass(tone);
  }

  protected highlightClass(tone: NotificationTone | undefined): string {
    return tone ? notificationTextToneClass(tone) : '';
  }

  protected unreadBadgeLabel(count: number): string {
    return count > 9 ? '9+' : String(count);
  }

  @HostListener('document:click', ['$event'])
  protected handleDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target || !this.open()) {
      return;
    }

    const menu = document.getElementById('header-notifications-menu');
    const toggle = document.getElementById('header-notifications-toggle');

    if (menu && toggle && !menu.contains(target) && !toggle.contains(target)) {
      this.handleClose();
    }
  }

  @HostListener('document:keydown.escape')
  protected handleEscape(): void {
    if (this.open()) {
      this.handleClose();
    }
  }
}
